import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { ContentPageHeader } from '@/components/global';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MachineSelect, useMachineCatalog } from '@/components/machines';
import { Save } from 'lucide-react';

type Mode = 'blank' | 'machine';

export default function PlanogramFormPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('blank');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetMargin, setTargetMargin] = useState('60');
  const [slotCount, setSlotCount] = useState('20');
  const [sourceMachineId, setSourceMachineId] = useState('');
  const { machines, loading: machinesLoading } = useMachineCatalog({
    isActive: true,
    enabled: mode === 'machine',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (!name.trim()) throw new Error('Name is required');
      const target = parseFloat(targetMargin);
      if (!Number.isFinite(target) || target < 0 || target > 100) {
        throw new Error('Target margin must be between 0 and 100');
      }

      const payload: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || undefined,
        targetMarginPercent: target,
      };

      if (mode === 'blank') {
        const count = parseInt(slotCount, 10);
        if (!Number.isFinite(count) || count < 1 || count > 100) {
          throw new Error('Slot count must be between 1 and 100');
        }
        payload.slotCount = count;
      } else {
        if (!sourceMachineId) throw new Error('Select a machine to seed from');
        payload.sourceMachineId = sourceMachineId;
      }

      const res = await apiFetch('/api/v1/planograms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
      }
      const created: { id: string } = await res.json();
      navigate(`/planograms/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create planogram');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title="New planogram"
        back={{ to: '/planograms', label: 'Planograms' }}
        actions={
          <Button type="submit" form="planogram-form" disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Creating…' : 'Create'}
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive whitespace-pre-line"
        >
          {error}
        </div>
      ) : null}

      <form id="planogram-form" onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Details</CardTitle>
            <CardDescription>Name and margin target for this draft.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Standard Gym Layout"
                required
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this layout."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetMargin">Target blended margin (%)</Label>
              <Input
                id="targetMargin"
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={targetMargin}
                onChange={(e) => setTargetMargin(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The whole-machine goal. Individual products may fall below it.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Starting point</CardTitle>
            <CardDescription>
              Start blank with a chosen number of slots, or copy the layout from an existing machine.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <TabsList>
                <TabsTrigger value="blank">Blank</TabsTrigger>
                <TabsTrigger value="machine">Seed from machine</TabsTrigger>
              </TabsList>
            </Tabs>

            {mode === 'blank' ? (
              <div className="space-y-2 sm:max-w-xs">
                <Label htmlFor="slotCount">
                  Slot count <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="slotCount"
                  type="number"
                  min={1}
                  max={100}
                  value={slotCount}
                  onChange={(e) => setSlotCount(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Slots are labelled A1–E5. Default capacity is 15 units per slot.
                </p>
              </div>
            ) : (
              <div className="space-y-2 sm:max-w-md">
                <Label htmlFor="sourceMachine">
                  Machine <span className="text-destructive">*</span>
                </Label>
                <MachineSelect
                  id="sourceMachine"
                  value={sourceMachineId}
                  onValueChange={setSourceMachineId}
                  machines={machines}
                  disabled={machinesLoading}
                  placeholder={machinesLoading ? 'Loading machines…' : 'Select machine'}
                />
                <p className="text-xs text-muted-foreground">
                  Copies the machine’s slots and product assignments as a starting point. Editing
                  the draft never changes the machine.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
